import { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { poolAPI } from '../lib/api';
import { useAuth } from '../lib/auth';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Trophy, Users, Shield, BarChart3, Zap, ArrowRight, Check } from 'lucide-react';

export default function InvitePage() {
  const { code } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [pool, setPool] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    const resolve = async () => {
      try {
        const res = await poolAPI.getByInvite(code);
        setPool(res.data.pool);
        // If already logged in, go straight to join
        if (user) {
          navigate(`/pools/${res.data.pool.id}/join`);
          return;
        }
      } catch {
        setError(true);
      } finally {
        setLoading(false);
      }
    };
    resolve();
  }, [code, user, navigate]);

  if (loading) return <div className="flex items-center justify-center h-screen"><div className="animate-spin h-8 w-8 border-4 border-[#5b3cc4] border-t-transparent rounded-full" /></div>;

  if (pool?.status === 'locked') return (
    <div className="min-h-screen flex items-center justify-center bg-background px-4">
      <Card className="max-w-md w-full text-center p-8">
        <div className="h-16 w-16 rounded-full bg-yellow-100 mx-auto mb-4 flex items-center justify-center">
          <Shield className="h-8 w-8 text-yellow-600" />
        </div>
        <h2 className="font-heading text-xl font-bold mb-2">Pool is Locked</h2>
        <p className="text-muted-foreground text-sm mb-4">"{pool.name}" is no longer accepting new entries. The season is in progress.</p>
        <Button asChild><Link to="/">Go to Pools FC</Link></Button>
      </Card>
    </div>
  );

  if (error) return (
    <div className="min-h-screen flex items-center justify-center bg-background px-4">
      <Card className="max-w-md w-full text-center p-8">
        <h2 className="font-heading text-xl font-bold mb-2">Invalid Invite Link</h2>
        <p className="text-muted-foreground text-sm mb-4">This invite link is no longer valid or the pool doesn't exist.</p>
        <Button asChild><Link to="/landing">Go to Pools FC</Link></Button>
      </Card>
    </div>
  );

  return (
    <div className="min-h-screen bg-background">
      {/* Nav */}
      <header className="border-b bg-white/95 backdrop-blur">
        <div className="max-w-[1200px] mx-auto px-4 sm:px-6 flex h-[64px] sm:h-[120px] items-center justify-between">
          <Link to="/landing" className="flex items-center gap-2 font-heading font-bold text-lg">
            <div className="h-8 w-8 rounded-lg bg-[#5b3cc4] flex items-center justify-center">
              <Trophy className="h-4 w-4 text-white" />
            </div>
            Pools FC
          </Link>
          <div className="flex items-center gap-3">
            <Link to="/login" className="text-sm font-medium text-muted-foreground hover:text-foreground">Sign In</Link>
            <Button asChild size="sm" className="bg-[#5b3cc4] hover:bg-[#4e33a8]">
              <Link to="/register">Get Started</Link>
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="max-w-[900px] mx-auto px-4 sm:px-6 py-12 sm:py-20">
        {/* Invite Card */}
        <div className="text-center mb-10">
          <Badge className="bg-[#f3f0ff] text-[#5b3cc4] font-medium mb-4">You've Been Invited</Badge>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight mb-3">
            Join <span className="text-[#5b3cc4]">{pool?.name || 'This Pool'}</span>
          </h1>
          <p className="text-lg text-muted-foreground max-w-lg mx-auto">
            You've been invited to join a Premier League prediction pool on Pools FC. Create an account to get started.
          </p>
        </div>

        {/* Pool Info Card */}
        <Card className="mb-10">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="h-12 w-12 rounded-xl bg-[#f3f0ff] flex items-center justify-center">
                <Trophy className="h-6 w-6 text-[#5b3cc4]" />
              </div>
              <div>
                <h2 className="font-heading text-xl font-bold">{pool?.name}</h2>
                <Badge className={pool?.status === 'open' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}>{pool?.status}</Badge>
              </div>
            </div>
            <p className="text-muted-foreground text-sm mb-6">
              Pick one team from each of 5 scoring tiers, predict total season goals, and compete to climb the leaderboard. Lower-tier picks earn more points — it's all about strategy.
            </p>
            <div className="flex flex-wrap gap-6 text-sm mb-6">
              <div><span className="text-muted-foreground">Entry Fee:</span> <strong>Details in pool</strong></div>
              <div><span className="text-muted-foreground">Max Entries:</span> <strong>2 per person</strong></div>
              <div><span className="text-muted-foreground">League:</span> <strong>English Premier League</strong></div>
            </div>
            <div className="flex flex-col sm:flex-row gap-3">
              <Button asChild size="lg" className="bg-[#5b3cc4] hover:bg-[#4e33a8] flex-1">
                <Link to={`/register`} onClick={() => { localStorage.setItem('pfc_intent', 'join_pool'); localStorage.setItem('pfc_selected_pool', pool?.id); }}>
                  Create Account & Join <ArrowRight className="h-4 w-4 ml-1" />
                </Link>
              </Button>
              <Button asChild variant="outline" size="lg" className="flex-1">
                <Link to={`/login`} onClick={() => { localStorage.setItem('pfc_intent', 'join_pool'); localStorage.setItem('pfc_selected_pool', pool?.id); }}>
                  Already Have an Account? Sign In
                </Link>
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* What is Pools FC */}
        <div className="text-center mb-8">
          <h3 className="font-heading text-xl font-bold mb-2">What is Pools FC?</h3>
          <p className="text-muted-foreground text-sm max-w-lg mx-auto">
            Your Premier League pool headquarters. Create or join prediction pools, pick teams across 5 scoring tiers, and track standings all season long.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-10">
          {[
            { icon: Zap, title: 'Quick Setup', desc: 'Create an account in seconds. Join a pool, pay, and pick your teams.' },
            { icon: BarChart3, title: 'Live Standings', desc: 'Real-time leaderboard with tier-by-tier breakdowns and week-by-week tracking.' },
            { icon: Shield, title: 'Fair & Transparent', desc: '5 scoring tiers, automated point calculation, goals prediction tiebreaker.' },
          ].map(f => (
            <Card key={f.title} className="text-center">
              <CardContent className="pt-6">
                <div className="h-10 w-10 rounded-xl bg-[#f3f0ff] flex items-center justify-center mx-auto mb-3">
                  <f.icon className="h-5 w-5 text-[#5b3cc4]" />
                </div>
                <h4 className="font-semibold text-sm mb-1">{f.title}</h4>
                <p className="text-xs text-muted-foreground">{f.desc}</p>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* How It Works */}
        <Card className="mb-10">
          <CardContent className="pt-6">
            <h3 className="font-heading text-lg font-bold mb-4 text-center">How It Works</h3>
            <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
              {[
                { step: 1, title: 'Create Account', desc: 'Sign up with your name and email' },
                { step: 2, title: 'Join & Pay', desc: 'Entry fee details provided by the pool organizer (collected offline)' },
                { step: 3, title: 'Pick Your Teams', desc: '1 team from each of the 5 scoring tiers' },
                { step: 4, title: 'Track & Compete', desc: 'Watch your points grow each matchweek' },
              ].map(s => (
                <div key={s.step} className="text-center">
                  <div className="h-8 w-8 rounded-full bg-[#5b3cc4] text-white flex items-center justify-center mx-auto mb-2 text-sm font-bold">{s.step}</div>
                  <h4 className="font-semibold text-sm mb-0.5">{s.title}</h4>
                  <p className="text-xs text-muted-foreground">{s.desc}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Final CTA */}
        <div className="text-center">
          <Button asChild size="lg" className="bg-[#5b3cc4] hover:bg-[#4e33a8] px-10">
            <Link to={`/register`} onClick={() => { localStorage.setItem('pfc_intent', 'join_pool'); localStorage.setItem('pfc_selected_pool', pool?.id); }}>
              Join {pool?.name} <ArrowRight className="h-4 w-4 ml-1" />
            </Link>
          </Button>
          <div className="flex justify-center gap-5 mt-4 text-xs text-muted-foreground">
            <span className="flex items-center gap-1"><Check className="h-3.5 w-3.5 text-[#5b3cc4]" /> No credit card required</span>
            <span className="flex items-center gap-1"><Check className="h-3.5 w-3.5 text-[#5b3cc4]" /> 30 second setup</span>
            <span className="flex items-center gap-1"><Check className="h-3.5 w-3.5 text-[#5b3cc4]" /> Free to join</span>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="border-t py-6">
        <div className="max-w-[1200px] mx-auto px-4 text-center">
          <p className="text-xs text-muted-foreground">Pools FC — Premier League prediction pools. More leagues coming soon.</p>
        </div>
      </footer>
    </div>
  );
}
